import { router } from '@inertiajs/svelte';
import type { FavoriteView, ViewConfig } from './types';
import { getFavoritePageType, getDefaultRangeForView } from './types';
import { getCurrentMonthKey, getCurrentWeekKey, getLocalToday, toDateStr } from './utils';

/**
 * Shared navigation logic for favorite views.
 * Handles both timeline and calendar favorites, used by:
 * - FavoritesDropdown (hover menu click)
 * - Dashboard (1-9 hotkeys)
 * - EventsCalendar (1-9 hotkeys)
 */
export function navigateToFavorite(fav: FavoriteView, viewConfig?: ViewConfig): void {
  const pageType = getFavoritePageType(fav);

  if (pageType === 'calendar') {
    navigateToCalendarFavorite(fav);
  } else {
    navigateToTimelineFavorite(fav, viewConfig);
  }
}

function navigateToCalendarFavorite(fav: FavoriteView): void {
  const year = new Date().getFullYear();
  const params = new URLSearchParams();

  // Only add layout param if it's not the default ('big-year')
  if (fav.layout && fav.layout !== 'big-year') {
    params.set('layout', fav.layout);
  }

  if (fav.layout === 'time-blocks') {
    const today = getLocalToday();
    const sunday = new Date(today);
    sunday.setUTCDate(sunday.getUTCDate() - sunday.getUTCDay());
    params.set('start', toDateStr(sunday));
    params.set('range', '7');
  }

  const query = params.toString();
  const url = `/events_calendar/${year}${query ? `?${query}` : ''}`;
  router.get(url, {}, { preserveScroll: true });
}

function navigateToTimelineFavorite(fav: FavoriteView, viewConfig?: ViewConfig): void {
  const view = fav.view!;
  const scale = fav.scale!;
  const params = new URLSearchParams();

  if (viewConfig) {
    const defaultView = viewConfig.defaultViewForScale[scale];
    if (view !== defaultView) {
      params.set('view', view);
    }

    if (fav.range) {
      const effectiveScale = view === 'days' ? 'week' : scale;
      const defaultRange = getDefaultRangeForView(view, effectiveScale, viewConfig);
      if (fav.range !== defaultRange) {
        params.set('range', fav.range as string);
      }
    }

    if (fav.display) {
      const defaultDisplay = viewConfig.defaultDisplay[view];
      if (fav.display !== defaultDisplay) {
        params.set('display', fav.display);
      }
    }
  } else {
    // No viewConfig available (e.g. navigating from calendar page to timeline)
    // Include all non-default params to be safe
    if (view) params.set('view', view);
    if (fav.range) params.set('range', fav.range as string);
    if (fav.display && fav.display !== 'both') params.set('display', fav.display);
  }

  const query = params.toString();
  const queryStr = query ? `?${query}` : '';

  let url: string;
  switch (scale) {
    case 'year':
      url = `/year/${new Date().getFullYear()}${queryStr}`;
      break;
    case 'week':
      url = `/week/${getCurrentWeekKey()}${queryStr}`;
      break;
    default:
      url = `/month/${getCurrentMonthKey()}${queryStr}`;
      break;
  }

  router.get(url, {}, { preserveScroll: true });
}
